import React, { createContext, useRef, useCallback, useMemo } from 'react';

export interface ServerRowModelDataSource<TRowData = any> {
    getRows: (params: GetRowsParams<TRowData>) => Promise<void>;
}

export interface GetRowsParams<TRowData = any> {
    request: {
        startRow: number;
        endRow: number;
        sortModel: any;
    };
    api: AgGridAPI<TRowData>;
    success: (params: { rowData: TRowData[], rowCount: number }) => void;
    fail: () => void;
}

export interface AgGridAPI<TRowData = any> {
    showLoadingOverlay: () => void;
    hideOverlay: () => void;
    refreshServerSide: () => void;
    isDestroyed: () => boolean;
    setGridOption: (gridOption: string, params: ServerRowModelDataSource<TRowData>) => void;
    refreshCells: (params: { force?: boolean }) => void;
    showNoRowsOverlay: () => void;
    paginationGetCurrentPage: () => number;
    paginationGoToPage: (pageNumber: number) => void;
    paginationGetPageSize: () => number;
}

interface Context<TRowData = any> {
    gridApiState: [api: React.MutableRefObject<AgGridAPI<TRowData>>, (api: AgGridAPI<TRowData>) => void];
    loadingState: [isLoading: React.MutableRefObject<boolean>, setIsLoading: (isLoading: boolean) => void];
}

export const AgGridContext = createContext<Context | null>(null);

export const AgGridProvider = <TRowData = any>({ children }: React.PropsWithChildren): React.ReactElement => {
    const gridApi = useRef<AgGridAPI<TRowData>>({} as any);
    const isLoadingRef = useRef<boolean>(true);
    
    const setGridApi = useCallback((api: AgGridAPI<TRowData>) => {
        gridApi.current = api;
    }, []);

    const setIsLoadingRef = (isLoading: boolean) => {
        isLoadingRef.current = isLoading;
    }

    const contextValue: Context<TRowData> = useMemo(() => ({
        gridApiState: [gridApi, setGridApi],
        loadingState: [isLoadingRef, setIsLoadingRef],
    }), [setGridApi]);

    return (
        <AgGridContext.Provider value={contextValue}>
            {children}
        </AgGridContext.Provider>
    );
};